<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-4">
      <v-text-field
        v-model="search"
        prepend-inner-icon="search"
        label="Buscar..."
        dense
        hide-details
        clearable
        class="search mr-4"
      ></v-text-field>
      <v-checkbox v-model="show_suggestions" label="Mostrar sugerencias" hide-details class="mt-0 mr-4"></v-checkbox>
      <v-spacer></v-spacer>
      <div v-if="editable" class="d-flex text-no-wrap">
        <v-btn outlined color="primary" :disabled="!suggestions.length || busy" @click="$emit('acceptSuggestions')">
          <v-icon left>auto_fix_high</v-icon> Aceptar sugerencias ({{ suggestions.length }})
        </v-btn>
        <v-btn outlined class="ml-2" color="purple darken-2" :disabled="!reglaLineas.length || busy" @click="askRules(reglaLineas)">
          <v-icon left>rule</v-icon> Registrar por reglas ({{ reglaLineas.length }})
        </v-btn>
      </div>
    </div>

    <!-- Nada pendiente -->
    <div v-if="!syscore.length && !lineas.length" class="text-center py-12">
      <v-icon size="56" color="success">check_circle</v-icon>
      <div class="text-h6 mt-2">No quedan movimientos por conciliar</div>
      <div class="body-2 grey--text">Revisá el resumen y guardá la conciliación.</div>
      <v-btn text color="primary" class="mt-2" @click="$emit('goResumen')">Ver resumen</v-btn>
    </div>

    <v-row v-else>
      <!-- Syscore -->
      <v-col cols="12" lg="6">
        <v-card outlined>
          <v-card-title class="card-head">
            <v-icon color="primary" class="mr-3 head-icon">dns</v-icon>
            <div>
              <div class="subtitle-1 font-weight-bold">Movimientos Syscore</div>
              <div class="body-2 grey--text text--darken-1">
                {{ syscore.length }} pendientes ·
                <span :class="totalPendSyscore < 0 ? 'error--text' : ''">{{ formatUtils.formatMoney(totalPendSyscore) }}</span>
              </div>
            </div>
          </v-card-title>
          <v-data-table
            v-model="selected_syscore"
            :headers="headersSyscore"
            :items="syscoreFiltered"
            :show-select="editable"
            item-key="id"
            :item-class="syscoreRowClass"
            :items-per-page="-1"
            hide-default-footer
            fixed-header
            height="calc(100vh - 320px)"
            dense
            class="compact"
            :loading="busy"
            loading-text="Cargando..."
            no-data-text="No hay movimientos de Syscore para mostrar"
          >
            <template v-slot:[`item.date`]="{ item }"><span class="text-no-wrap">{{ formatUtils.formatDateString(item.date) }}</span></template>
            <template v-slot:[`item.movimiento`]="{ item }">
              <div class="py-1 text-break">
                <div>{{ item.tipo }} · {{ item.comprobante }}</div>
                <div class="caption grey--text text--darken-1">{{ item.tercero || item.description || "" }}</div>
                <v-chip v-if="item.arrastre" x-small color="blue lighten-5" text-color="blue darken-3" class="mt-1">Partida anterior</v-chip>
                <v-chip v-if="item.reverted" x-small color="grey lighten-3" class="mt-1 ml-1">Revertido</v-chip>
                <div v-if="show_suggestions && suggestionByDetail[item.id]" class="caption mt-1">
                  <v-icon x-small color="orange darken-3">lightbulb</v-icon>
                  <a class="orange--text text--darken-3" title="Seleccionar el par sugerido" @click.stop="selectSuggestion(item)">
                    Sugerido · Banco {{ formatUtils.formatDateString(suggestionByDetail[item.id].linea.date).substr(0, 5) }}
                  </a>
                  <span class="orange--text text--darken-3">({{ reasonLabel(suggestionByDetail[item.id].reason) }})</span>
                </div>
              </div>
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              <span class="text-no-wrap" :class="item.amount < 0 ? 'error--text' : ''">{{ formatUtils.formatMoney(item.amount) }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-menu v-if="editable" offset-y left>
                <template v-slot:activator="{ on }">
                  <v-btn icon small :disabled="busy" v-on="on"><v-icon>more_vert</v-icon></v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="$emit('partida', [item])">
                    <v-list-item-icon><v-icon color="orange darken-2">pending_actions</v-icon></v-list-item-icon>
                    <v-list-item-title>Marcar como partida conciliatoria</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="suggestionByDetail[item.id]" @click="$emit('acceptSuggestions', [suggestionByDetail[item.id].linea.id])">
                    <v-list-item-icon><v-icon color="primary">auto_fix_high</v-icon></v-list-item-icon>
                    <v-list-item-title>Aceptar sugerencia</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Banco -->
      <v-col cols="12" lg="6">
        <v-card outlined>
          <v-card-title class="card-head">
            <v-icon color="primary" class="mr-3 head-icon">account_balance</v-icon>
            <div>
              <div class="subtitle-1 font-weight-bold">Movimientos banco</div>
              <div class="body-2 grey--text text--darken-1">
                {{ lineas.length }} pendientes ·
                <span :class="totalPendBanco < 0 ? 'error--text' : ''">{{ formatUtils.formatMoney(totalPendBanco) }}</span>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="vista_banco" mandatory dense class="ml-3">
              <v-btn small value="agrupada">Agrupada</v-btn>
              <v-btn small value="detalle">Detalle</v-btn>
            </v-btn-toggle>
          </v-card-title>

          <!-- Vista agrupada -->
          <v-data-table
            v-if="vista_banco === 'agrupada'"
            :headers="headersGrupos"
            :items="grupos"
            item-key="key"
            show-expand
            :items-per-page="-1"
            hide-default-footer
            fixed-header
            height="calc(100vh - 320px)"
            dense
            class="compact"
            :loading="busy"
            loading-text="Cargando..."
            no-data-text="No hay movimientos del banco para mostrar"
          >
            <template v-slot:[`item.sel`]="{ item }">
              <v-simple-checkbox
                :value="groupAllSelected(item)"
                :indeterminate="groupSomeSelected(item)"
                color="primary"
                :ripple="false"
                @input="toggleGroup(item, $event)"
              ></v-simple-checkbox>
            </template>
            <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
              <v-btn v-if="item.lineas.length > 1" icon small @click="expand(!isExpanded)">
                <v-icon>{{ isExpanded ? "expand_less" : "expand_more" }}</v-icon>
              </v-btn>
            </template>
            <template v-slot:[`item.concept`]="{ item }">
              <div class="py-1 text-break">
                <div>
                  {{ item.lineas[0].concept }}
                  <v-chip v-if="item.lineas.length > 1" x-small class="ml-1" :title="item.lineas.length + ' movimientos'">{{ item.lineas.length }}</v-chip>
                </div>
                <div class="caption grey--text text--darken-1">
                  {{ groupDates(item) }} · {{ item.lineas.length === 1 ? item.lineas[0].reference || item.lineas[0].bank_code || "" : item.lineas[0].bank_code || "" }}
                </div>
                <v-chip v-if="item.regla" x-small color="purple lighten-5" text-color="purple darken-2" class="mt-1">
                  <v-icon x-small left>rule</v-icon>{{ item.regla.tesoreria_movement_template.name }}
                </v-chip>
              </div>
            </template>
            <template v-slot:[`item.total`]="{ item }">
              <span class="text-no-wrap" :class="item.total < 0 ? 'error--text' : ''">{{ formatUtils.formatMoney(item.total) }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn v-if="item.regla" icon small color="purple darken-2" :disabled="busy" v-on="on" @click="askRules(item.lineas)"><v-icon>rule</v-icon></v-btn>
                  <v-btn v-else icon small color="primary" :disabled="busy" v-on="on" @click="$emit('createMovement', item.lineas)"><v-icon>swap_vert</v-icon></v-btn>
                </template>
                <span>{{ item.regla ? "Registrar con regla" : "Registrar (crear movimiento de tesorería)" }}</span>
              </v-tooltip>
              <v-menu offset-y left>
                <template v-slot:activator="{ on }">
                  <v-btn icon small :disabled="busy" v-on="on"><v-icon>more_vert</v-icon></v-btn>
                </template>
                <v-list dense>
                  <v-list-item v-if="!item.regla" @click="$emit('createRule', item.lineas[0])">
                    <v-list-item-icon><v-icon color="purple darken-2">rule</v-icon></v-list-item-icon>
                    <v-list-item-title>Crear regla a partir de este grupo</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-else @click="$emit('createMovement', item.lineas)">
                    <v-list-item-icon><v-icon color="primary">swap_vert</v-icon></v-list-item-icon>
                    <v-list-item-title>Crear mov. de tesorería</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0 grey lighten-5">
                <div class="expanded-lines">
                  <v-simple-table dense class="grey lighten-5">
                    <tbody>
                      <tr v-for="l in item.lineas" :key="l.id">
                        <td class="col-check">
                          <v-simple-checkbox :value="isBancoSelected(l)" color="primary" :ripple="false" @input="toggleLinea(l, $event)"></v-simple-checkbox>
                        </td>
                        <td class="col-date">{{ formatUtils.formatDateString(l.date) }}</td>
                        <td class="caption grey--text text--darken-1">{{ l.reference || l.concept }}</td>
                        <td class="text-right" :class="l.amount < 0 ? 'error--text' : ''">{{ formatUtils.formatMoney(l.amount) }}</td>
                        <td v-if="editable" class="col-actions"></td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </td>
            </template>
          </v-data-table>

          <!-- Vista detalle -->
          <v-data-table
            v-else
            v-model="selected_banco"
            :headers="headersBanco"
            :items="lineasFiltered"
            :show-select="editable"
            item-key="id"
            :item-class="bancoRowClass"
            :items-per-page="-1"
            hide-default-footer
            fixed-header
            height="calc(100vh - 320px)"
            dense
            class="compact"
            :loading="busy"
            loading-text="Cargando..."
            no-data-text="No hay movimientos del banco para mostrar"
          >
            <template v-slot:[`item.date`]="{ item }"><span class="text-no-wrap">{{ formatUtils.formatDateString(item.date) }}</span></template>
            <template v-slot:[`item.concept`]="{ item }">
              <div class="py-1 text-break">
                <div>{{ item.concept }}</div>
                <div class="caption grey--text text--darken-1">{{ item.reference || item.bank_code || "" }}</div>
                <v-chip v-if="item.regla" x-small color="purple lighten-5" text-color="purple darken-2" class="mt-1">
                  <v-icon x-small left>rule</v-icon>{{ item.regla.tesoreria_movement_template.name }}
                </v-chip>
              </div>
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              <span class="text-no-wrap" :class="item.amount < 0 ? 'error--text' : ''">{{ formatUtils.formatMoney(item.amount) }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-menu v-if="editable" offset-y left>
                <template v-slot:activator="{ on }">
                  <v-btn icon small :disabled="busy" v-on="on"><v-icon>more_vert</v-icon></v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="$emit('createMovement', [item])">
                    <v-list-item-icon><v-icon color="primary">swap_vert</v-icon></v-list-item-icon>
                    <v-list-item-title>Crear mov. de tesorería</v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item @click="$emit('createRule', item)">
                    <v-list-item-icon><v-icon color="purple darken-2">rule</v-icon></v-list-item-icon>
                    <v-list-item-title>Crear regla a partir de esta línea</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Barra de selección -->
    <v-sheet v-if="editable && hasSelection" elevation="4" rounded class="selection-bar white d-flex align-center flex-wrap px-4 py-2 mt-3">
      <div v-if="selected_syscore.length" class="mr-6">
        <div class="caption">Syscore ({{ selected_syscore.length }})</div>
        <div class="subtitle-1 font-weight-medium">{{ formatUtils.formatMoney(totalSyscore) }}</div>
      </div>
      <v-icon v-if="selected_syscore.length && selected_banco.length" class="mr-6">compare_arrows</v-icon>
      <div v-if="selected_banco.length" class="mr-6">
        <div class="caption">Banco ({{ selected_banco.length }})</div>
        <div class="subtitle-1 font-weight-medium">{{ formatUtils.formatMoney(totalBanco) }}</div>
      </div>
      <template v-if="selected_syscore.length && selected_banco.length">
        <v-divider vertical class="mr-6"></v-divider>
        <div>
          <div class="caption">Diferencia</div>
          <div class="subtitle-1 font-weight-medium" :class="diferenciaOk ? 'success--text' : 'error--text'">
            {{ formatUtils.formatMoney(diferencia) }}
            <v-icon v-if="diferenciaOk" color="success">check_circle</v-icon>
          </div>
        </div>
      </template>
      <v-spacer></v-spacer>
      <v-btn text color="primary" @click="clearSelection">Limpiar selección</v-btn>
      <template v-if="selected_syscore.length && !selected_banco.length">
        <v-btn outlined color="orange darken-2" class="ml-2" :disabled="busy" @click="$emit('partida', selected_syscore)">
          <v-icon left>pending_actions</v-icon> Marcar como partida
        </v-btn>
        <v-btn v-if="selected_syscore.length > 1 && diferenciaOk" color="success" class="ml-2" :loading="busy" @click="match">
          <v-icon left>link</v-icon> Conciliar
        </v-btn>
      </template>
      <v-tooltip v-else-if="selected_banco.length && !selected_syscore.length" top>
        <template v-slot:activator="{ on }">
          <span v-on="on" class="ml-2">
            <v-btn color="primary" :disabled="mixedSigns || busy" @click="$emit('createMovement', selected_banco)">
              <v-icon left>swap_vert</v-icon> Registrar
            </v-btn>
          </span>
        </template>
        <span>{{ mixedSigns ? "Créditos y débitos no pueden ir en un mismo movimiento" : "Crear movimiento de tesorería" }}</span>
      </v-tooltip>
      <v-tooltip v-else top :disabled="diferenciaOk">
        <template v-slot:activator="{ on }">
          <span v-on="on" class="ml-2">
            <v-btn color="success" :loading="busy" :disabled="!diferenciaOk" @click="match">
              <v-icon left>link</v-icon> Conciliar selección
            </v-btn>
          </span>
        </template>
        <span>La diferencia debe ser {{ formatUtils.formatMoney(0) }}</span>
      </v-tooltip>
    </v-sheet>

    <DialogYesNo
      :open="dialog_rules"
      title="Registrar por reglas"
      :message="rulesMessage"
      nameCancel="No"
      nameAccept="Sí"
      @accept="confirmRules"
      @cancel="dialog_rules = false"
    />
  </div>
</template>

<script>
import { formatUtils } from "@/libs/utils/formatUtils";
import DialogYesNo from "@/components/DialogYesNo.vue";

const REASONS = { "fecha-monto": "fecha-monto", "fecha-aprox": "monto, fecha cercana", cuit: "CUIT", cheque: "nº de cheque" };

export default {
  components: { DialogYesNo },
  props: {
    syscore: { type: Array, default: () => [] },
    lineas: { type: Array, default: () => [] },
    suggestions: { type: Array, default: () => [] },
    editable: Boolean,
    busy: Boolean,
  },
  data() {
    return {
      formatUtils,
      search: "",
      show_suggestions: true,
      vista_banco: "agrupada",
      selected_syscore: [],
      selected_banco: [],
      dialog_rules: false,
      rules_lineas: [],
      headersSyscore: [
        { text: "Fecha", value: "date", width: 90 },
        { text: "Movimiento", value: "movimiento", sortable: false },
        { text: "Importe", value: "amount", align: "right" },
        { text: "", value: "actions", sortable: false, width: 40 },
      ],
      headersBanco: [
        { text: "Fecha", value: "date", width: 90 },
        { text: "Descripción", value: "concept" },
        { text: "Importe", value: "amount", align: "right" },
        { text: "", value: "actions", sortable: false, width: 40 },
      ],
    };
  },
  computed: {
    headersGrupos() {
      const headers = [];
      if (this.editable) headers.push({ text: "", value: "sel", sortable: false, width: 40 });
      headers.push(
        { text: "", value: "data-table-expand", sortable: false, width: 40 },
        { text: "Descripción", value: "concept", sortable: false },
        { text: "Importe", value: "total", sortable: false, align: "right" }
      );
      if (this.editable) headers.push({ text: "", value: "actions", sortable: false, align: "right", width: 80 });
      return headers;
    },
    suggestionByDetail() {
      const map = {};
      this.suggestions.forEach((s) => {
        const linea = this.lineas.find((l) => l.id === s.linea_id);
        if (linea) map[s.detail_id] = { linea, reason: s.reason };
      });
      return map;
    },
    suggestionByLinea() {
      const map = {};
      this.suggestions.forEach((s) => (map[s.linea_id] = s));
      return map;
    },
    syscoreFiltered() {
      return this.applySearch(this.syscore, (d) => [d.tipo, d.comprobante, d.tercero, d.description, d.concept, d.reference]);
    },
    lineasFiltered() {
      return this.applySearch(this.lineas, (l) => [l.concept, l.reference, l.bank_code]);
    },
    // Líneas del banco agrupadas por código/descripción y signo (la búsqueda filtra antes de agrupar)
    grupos() {
      const map = {};
      this.lineasFiltered.forEach((l) => {
        const key = (l.bank_code || (l.concept || "").trim().toLowerCase()) + (l.amount < 0 ? "-" : "+");
        (map[key] = map[key] || { key, lineas: [] }).lineas.push(l);
      });
      return Object.values(map)
        .map((g) => ({
          ...g,
          total: this.round(g.lineas.reduce((s, l) => s + l.amount, 0)),
          regla: g.lineas.every((l) => l.regla) ? g.lineas[0].regla : null,
        }))
        .sort((a, b) => b.lineas.length - a.lineas.length || Math.abs(b.total) - Math.abs(a.total));
    },
    totalPendSyscore() {
      return this.round(this.syscore.reduce((s, d) => s + d.amount, 0));
    },
    totalPendBanco() {
      return this.round(this.lineas.reduce((s, l) => s + l.amount, 0));
    },
    totalSyscore() {
      return this.round(this.selected_syscore.reduce((s, d) => s + d.amount, 0));
    },
    totalBanco() {
      return this.round(this.selected_banco.reduce((s, l) => s + l.amount, 0));
    },
    diferencia() {
      return this.round(this.totalBanco - this.totalSyscore);
    },
    diferenciaOk() {
      return Math.abs(this.diferencia) < 0.01;
    },
    hasSelection() {
      return this.selected_syscore.length > 0 || this.selected_banco.length > 0;
    },
    mixedSigns() {
      return this.selected_banco.some((l) => l.amount > 0) && this.selected_banco.some((l) => l.amount < 0);
    },
    selectedBancoIds() {
      return this.selected_banco.map((l) => l.id);
    },
    reglaLineas() {
      return this.lineasFiltered.filter((l) => l.regla);
    },
    rulesMessage() {
      const total = this.rules_lineas.reduce((s, l) => s + Math.abs(l.amount), 0);
      return (
        "Se van a crear movimientos de tesorería para " +
        this.rules_lineas.length +
        " líneas del banco por " +
        formatUtils.formatMoney(total) +
        " y quedarán conciliados. ¿Continuar?"
      );
    },
  },
  watch: {
    syscore() {
      const ids = this.syscore.map((d) => d.id);
      this.selected_syscore = this.selected_syscore.filter((d) => ids.includes(d.id));
    },
    lineas() {
      const ids = this.lineas.map((l) => l.id);
      this.selected_banco = this.selected_banco.filter((l) => ids.includes(l.id));
    },
  },
  created() {
    this.vista_banco = this.grupos.some((g) => g.lineas.length > 1) ? "agrupada" : "detalle";
  },
  methods: {
    round(v) {
      return Math.round(v * 100) / 100;
    },
    reasonLabel(reason) {
      return REASONS[reason] || reason;
    },
    applySearch(items, fields) {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return items;
      return items.filter((i) =>
        fields(i)
          .concat([String(i.amount), formatUtils.formatNumber(i.amount), formatUtils.formatDateString(i.date)])
          .some((v) => v && String(v).toLowerCase().includes(q))
      );
    },
    syscoreRowClass(item) {
      return this.show_suggestions && this.suggestionByDetail[item.id] ? "amber lighten-5" : "";
    },
    bancoRowClass(item) {
      return this.show_suggestions && this.suggestionByLinea[item.id] ? "amber lighten-5" : "";
    },
    selectSuggestion(item) {
      const s = this.suggestionByDetail[item.id];
      if (!s) return;
      this.selected_syscore = [item];
      this.selected_banco = [s.linea];
    },
    groupDates(group) {
      const dates = group.lineas.map((l) => l.date).sort();
      const short = (d) => formatUtils.formatDateString(d).substr(0, 5);
      const from = short(dates[0]);
      const to = short(dates[dates.length - 1]);
      return from === to ? from : from + " – " + to;
    },
    isBancoSelected(linea) {
      return this.selectedBancoIds.includes(linea.id);
    },
    groupAllSelected(group) {
      return group.lineas.every((l) => this.isBancoSelected(l));
    },
    groupSomeSelected(group) {
      return !this.groupAllSelected(group) && group.lineas.some((l) => this.isBancoSelected(l));
    },
    toggleGroup(group, value) {
      const ids = group.lineas.map((l) => l.id);
      const rest = this.selected_banco.filter((l) => !ids.includes(l.id));
      this.selected_banco = value ? rest.concat(group.lineas) : rest;
    },
    toggleLinea(linea, value) {
      const rest = this.selected_banco.filter((l) => l.id !== linea.id);
      this.selected_banco = value ? rest.concat([linea]) : rest;
    },
    askRules(lineas) {
      this.rules_lineas = lineas;
      this.dialog_rules = true;
    },
    confirmRules() {
      this.dialog_rules = false;
      this.$emit("createByRules", this.rules_lineas);
    },
    clearSelection() {
      this.selected_syscore = [];
      this.selected_banco = [];
    },
    match() {
      this.$emit("match", {
        linea_ids: this.selected_banco.map((l) => l.id),
        detail_ids: this.selected_syscore.map((d) => d.id),
      });
    },
  },
};
</script>

<style scoped>
/* Media pantalla: menos padding para que las tablas no scrolleen horizontal */
.compact >>> th,
.compact >>> td {
  padding: 0 8px !important;
}
/* El header fijo de la tabla no debe pasar por encima de la barra de título de la página (z-index 2) */
.compact >>> th {
  z-index: 1 !important;
}
/* Misma altura en ambos títulos para que las tablas queden alineadas */
.card-head {
  align-items: flex-start;
  flex-wrap: nowrap;
  height: 72px;
  padding: 12px 16px;
}
.head-icon {
  margin-top: 2px;
}
.search {
  min-width: 280px;
  max-width: 420px;
}
.selection-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
}
.expanded-lines {
  padding-left: 48px;
}
.expanded-lines .col-check {
  width: 40px;
}
.expanded-lines .col-actions {
  width: 80px;
}
.expanded-lines .col-date {
  width: 100px;
}
</style>
